/**
 * Zustand Store für Open Source Projekte
 * Verwaltet den Ladezustand und die Daten der Projekte
 */

import { create } from 'zustand';

const CMS_URL = 'https://cms.simplify-data.de';
const STORAGE_URL = 'https://eu2.contabostorage.com/4b109eb0bb514dacbd35c4a46d016bc8:directus/';

// Helper: Get file extension from MIME type
const getExtension = (mimeType) => {
    if (!mimeType) return '';
    const ext = mimeType.split('/')[1];
    return ext === 'jpeg' ? '.jpg' : `.${ext}`;
};

// Helper: Build image URL
const buildImageUrl = (image) => {
    if (!image) return '/images/opensource/opensource-projects.webp';
    if (typeof image === 'object' && image.id) {
        return `${STORAGE_URL}${image.id}${getExtension(image.type)}`;
    }
    if (typeof image === 'string') {
        return image.match(/^[a-f0-9-]{36}$/i) ? `${STORAGE_URL}${image}` : image;
    }
    return '/images/opensource/opensource-projects.webp';
};

const useOpenSourceStore = create((set, get) => ({
    // State
    projects: [],
    isLoading: false,
    error: null,
    lastFetched: null,

    // Actions
    fetchProjects: async () => {
        // Wenn bereits Daten vorhanden und weniger als 60 Sekunden alt, nicht neu laden
        const { lastFetched, projects } = get();
        const now = Date.now();
        if (lastFetched && projects.length > 0 && (now - lastFetched) < 60000) {
            return;
        }

        set({ isLoading: true, error: null });

        try {
            // Nur Einträge mit status=published abrufen
            const response = await fetch(`${CMS_URL}/items/opensource?fields=*,translations.*,image.*&filter[status][_eq]=published`);
            
            if (!response.ok) {
                console.error('Error fetching opensource projects: Response not ok');
                set({ projects: [], isLoading: false, error: 'Failed to fetch projects' });
                return;
            }

            const data = await response.json();
            const rawProjects = data.data || [];
            
            // Transform projects to include proper image URLs
            const transformedProjects = rawProjects.map(project => ({
                ...project,
                imageUrl: buildImageUrl(project.image)
            }));
            
            set({ 
                projects: transformedProjects, 
                isLoading: false, 
                error: null,
                lastFetched: Date.now()
            });
        } catch (error) {
            console.error('Error fetching opensource projects:', error);
            set({ projects: [], isLoading: false, error: error.message });
        }
    },

    // Projekt nach Slug finden
    getProjectBySlug: (slug) => {
        const { projects } = get();
        return projects.find(project => {
            // Slug aus ID extrahieren (letzte Zahl nach dem letzten Bindestrich)
            const projectId = slug.split('-').pop();
            return String(project.id) === String(projectId);
        }) || null;
    },

    // Legacy method: Projekt nach ID finden
    getProjectById: (id) => {
        const { projects } = get();
        return projects.find(project => String(project.id) === String(id)) || null;
    },

    // Projekte nach Typ filtern
    getProjectsByType: (type) => {
        const { projects } = get();
        return projects.filter(project => project.contributionType === type);
    },

    // Einzigartige Sprachen abrufen
    getLanguages: () => {
        const { projects } = get();
        const uniqueLanguages = [...new Set(projects.map(project => project.language))];
        return uniqueLanguages.filter(Boolean).sort();
    },

    // State zurücksetzen
    reset: () => {
        set({ projects: [], isLoading: false, error: null, lastFetched: null });
    }
}));

export default useOpenSourceStore;
